import { I18nTranslator } from '@strength/i18n';
import { sortByOrder } from '@strength/utils';
import { getWorkoutName } from '@strength/domain';
import { Block } from '../../plan/timeline/types';
import { Workout } from '@strength/domain-workout';

// Add properties here to compare for sections
const sectionProps = ['name', 'instruction'] as const;

// Add properties here to compare for exercises
const exerciseProps = [
  'exerciseName',
  'libraryExerciseId',
  'isEachSide',
  'instruction',
] as const;

type SectionProp = (typeof sectionProps)[number];
type ExerciseProp = (typeof exerciseProps)[number];

type SectionRecord = Record<SectionProp, unknown>;
type ExerciseRecord = Record<ExerciseProp, unknown>;

// The properties we need to build a block name
export type BlockName = Pick<Block, 'name' | 'cycleLabel' | 'sessionLabel'>;

export type ComparableWorkout = Pick<
  Workout,
  'workoutId' | 'name' | 'sections'
> & {
  cycleSortOrder: number;
  sessionSortOrder: number;
};

/**
 * Merged workout layers apply nullable overrides, so a field the coach never
 * filled in can arrive as null, undefined, or an empty string depending on
 * which layer supplied it. Collapse those to a single value so equivalent
 * prescriptions are not reported as mismatches.
 */
function normalizeProp(value: unknown): unknown {
  if (value === undefined) return null;
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function extractProps<T, P extends string & keyof T>(
  entity: T,
  props: readonly P[],
): Record<P, unknown> {
  return props.reduce(
    (acc, prop) => {
      acc[prop as P] = normalizeProp(entity[prop as P]);
      return acc;
    },
    {} as Record<P, unknown>,
  );
}

function extractSectionsAndExercises(
  block: BlockName,
  workout: ComparableWorkout,
) {
  const sectionRecords: SectionRecord[] = [];
  const exerciseRecords: ExerciseRecord[] = [];

  sortByOrder(workout.sections).forEach((section) => {
    sectionRecords.push(extractProps(section, sectionProps));

    sortByOrder(section.entries).forEach((entry) => {
      if (entry.__type === 'WorkoutExercise') {
        exerciseRecords.push(extractProps(entry, exerciseProps));
      } else {
        // Can't use sortByOrder here due to entry.exercises being a union type
        const exercises = [...entry.exercises].sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
        exerciseRecords.push(
          ...exercises.map(({ exercise }) =>
            extractProps(exercise, exerciseProps),
          ),
        );
      }
    });
  });

  const { cycleSortOrder, sessionSortOrder, name } = workout;
  const cycleSessionData = {
    cycleSortOrder: cycleSortOrder,
    sessionSortOrder: sessionSortOrder,
  };
  return {
    name,
    // We need a unique name for each workout so our errors are specific enough
    cycleSessionName: getWorkoutName(cycleSessionData, block, false),
    sections: sectionRecords,
    exercises: exerciseRecords,
  };
}

function compare<T extends object>(a: T, b: T) {
  const keys = Object.keys(a) as (keyof T)[];
  const allMatch = keys.every((key) => {
    // It is acceptable if other workouts do not have a property set at all
    // in these cases, we just use the value from the template workout
    const match = b[key] === undefined || b[key] === null || a[key] === b[key];
    return match;
  });
  return allMatch;
}

export function compareWorkouts(
  t: I18nTranslator,
  block: BlockName,
  ...workouts: ComparableWorkout[]
): Record<string, string[]> {
  const sortedWorkouts = workouts.sort(
    (a, b) => a.cycleSortOrder - b.cycleSortOrder,
  );

  if (!sortedWorkouts[0]) return {};

  // The first workout will act as the template to compare the rest to
  const [template, ...rest] = sortedWorkouts;

  if (rest.length === 0) return {};

  const { sections: templateSections, exercises: templateExercises } =
    extractSectionsAndExercises(block, template);

  // Extract all workout data upfront
  const workoutDataMap = new Map<
    string,
    {
      name: string | null;
      cycleSessionName: string;
      sections: SectionRecord[];
      exercises: ExerciseRecord[];
    }
  >();
  const errorsByWorkoutId: Record<string, string[]> = {};

  rest.forEach((workout) => {
    const workoutData = extractSectionsAndExercises(block, workout);
    workoutDataMap.set(workout.workoutId, workoutData);
    errorsByWorkoutId[workout.workoutId] = [];
  });

  // Compare sections
  templateSections.forEach((templateSection, index) => {
    workoutDataMap.forEach(({ cycleSessionName, sections }, workoutId) => {
      const section = sections[index];
      const errors = errorsByWorkoutId[workoutId];
      if (!errors) return;

      if (!section) {
        errors.push(
          t('workoutMissingSection', {
            workout: cycleSessionName,
            section: templateSection.name,
          }),
        );
        return;
      }

      if (!compare(templateSection, section)) {
        errors.push(
          t('workoutSectionMismatch', {
            workout: cycleSessionName,
            section: templateSection.name,
          }),
        );
      }
    });
  });

  // Compare exercises
  templateExercises.forEach((templateExercise, index) => {
    workoutDataMap.forEach(({ cycleSessionName, exercises }, workoutId) => {
      const exercise = exercises[index];
      const errors = errorsByWorkoutId[workoutId];
      if (!errors) return;

      if (!exercise) {
        errors.push(
          t('workoutMissingExercise', {
            workout: cycleSessionName,
            exercise: templateExercise.exerciseName,
          }),
        );
        return;
      }

      if (!compare(templateExercise, exercise)) {
        errors.push(
          t('workoutExerciseMismatch', {
            workout: cycleSessionName,
            exercise: exercise.exerciseName,
          }),
        );
      }
    });
  });

  // Check for extra sections/exercises in each workout
  workoutDataMap.forEach(
    ({ cycleSessionName, sections, exercises }, workoutId) => {
      const errors = errorsByWorkoutId[workoutId];
      if (!errors) return;

      // Check if workout has extra sections
      if (sections.length > templateSections.length) {
        errors.push(
          t('workoutHasExtraSections', { workout: cycleSessionName }),
        );
      }

      // Check if workout has extra exercises
      if (exercises.length > templateExercises.length) {
        errors.push(
          t('workoutHasExtraExercises', { workout: cycleSessionName }),
        );
      }
    },
  );

  return errorsByWorkoutId;
}
